import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useAdmin } from '../context/AdminContext';
import { Button, Input, Select, Textarea, Modal, Spinner, Badge, formatPrice, formatMileage, formatDate, timeAgo, useToast, T } from '../components/UI';

const MAKES = ['Volkswagen','BMW','Audi','Toyota','Ford','Mercedes-Benz','Hyundai','Nissan','Kia','Skoda','Peugeot','Renault','Opel','Volvo','Honda','SEAT','Mazda','Suzuki','Mini','Fiat','Tesla','Land Rover','Jaguar','Jeep'];
const BODY_TYPES = ['Hatchback','Saloon','SUV','Estate','Coupe','Convertible','MPV','Van'];
const FUEL_TYPES = ['Petrol','Diesel','Electric','Petrol Hybrid','Diesel Hybrid','Petrol Plug-in Hybrid'];
const TRANSMISSIONS = ['Manual','Automatic','Semi-Automatic'];
const COLOURS = ['Black','White','Silver','Grey','Blue','Red','Green','Orange','Yellow','Brown','Beige'];
const YEARS = Array.from({ length: 12 }, (_, i) => 2025 - i);
const FEATURES_LIST = ['Heated Seats','Parking Sensors','Reversing Camera','Bluetooth','Cruise Control','Sat Nav','Panoramic Roof','Leather Interior','Keyless Entry','Climate Control','Apple CarPlay','Android Auto','Lane Assist','Blind Spot Monitor','Heated Steering Wheel','Sunroof','Alloy Wheels','Adaptive Cruise','Wireless Charging','360 Camera','Electric Seats','Head-Up Display'];

/* ── Admin Login ── */
function AdminLogin() {
  const { login } = useAdmin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); }
    catch { setError('Invalid email or password'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: T.bg, padding: '48px 44px', width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ width: 44, height: 44, background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ color: '#fff', fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 16 }}>NA</span>
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-head)', fontWeight: 400, color: T.ink, marginBottom: 4 }}>Admin Panel</h1>
          <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.06em' }}>Northbridge Automotives</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@northbridgeauto.ie" required />
            <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required />
            {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', color: '#991b1b', fontSize: 'var(--step-sm)' }}>{error}</div>}
            <Button type="submit" fullWidth size="lg" loading={loading}>Sign in</Button>
          </div>
        </form>
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Link to="/" style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em' }}>← Back to website</Link>
        </div>
      </div>
    </div>
  );
}

/* ── Listing Form ── */
function ListingForm({ initial = {}, onSubmit, loading: saving, onCancel }) {
  const BLANK = { make:'', model:'', year:'', bodyType:'', fuelType:'', transmission:'', engine:'', colour:'', mileage:'', price:'', description:'', nct:'', seats:'5', doors:'4', features:[], status:'available' };
  const [form, setForm] = useState({ ...BLANK, ...initial });
  const set = k => e => setForm(f => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));
  const toggleFeat = f => setForm(p => ({ ...p, features: p.features?.includes(f) ? p.features.filter(x => x !== f) : [...(p.features || []), f] }));

  const selS = { border: `1px solid ${T.surface}`, background: T.bg, padding: '9px 28px 9px 12px', fontSize: 'var(--step-sm)', color: T.ink, appearance: 'none', cursor: 'pointer', width: '100%', fontFamily: 'inherit', outline: 'none' };
  const Sel = ({ label, field, opts }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <select style={selS} value={form[field]} onChange={set(field)}>
          <option value="">Select {label.toLowerCase()}</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 9 }}>▼</span>
      </div>
    </div>
  );

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form); }} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Status toggle */}
      <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: 14 }}>Status</span>
        {['available','reserved','sold'].map(s => (
          <button key={s} type="button" onClick={() => setForm(f => ({ ...f, status: s }))}
            style={{ padding: '7px 18px', border: `1px solid ${form.status === s ? T.accent : T.surface}`, background: form.status === s ? T.accent : 'transparent', color: form.status === s ? T.accentInk : T.muted, fontSize: 'var(--step-xs)', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize', letterSpacing: '0.04em', fontFamily: 'inherit', transition: 'all 0.15s', marginRight: 2 }}>
            {s}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Sel label="Make" field="make" opts={MAKES} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Model</label>
          <input style={{ ...selS, paddingRight: 12 }} value={form.model} onChange={set('model')} placeholder="e.g. Golf, X5" />
        </div>
        <Sel label="Year" field="year" opts={YEARS} />
        <Sel label="Body Type" field="bodyType" opts={BODY_TYPES} />
        <Sel label="Fuel Type" field="fuelType" opts={FUEL_TYPES} />
        <Sel label="Transmission" field="transmission" opts={TRANSMISSIONS} />
        <Sel label="Colour" field="colour" opts={COLOURS} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Engine</label>
          <input style={{ ...selS, paddingRight: 12 }} value={form.engine} onChange={set('engine')} placeholder="e.g. 1.5L" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Mileage (km)</label>
          <input style={{ ...selS, paddingRight: 12 }} type="number" value={form.mileage} onChange={set('mileage')} placeholder="e.g. 45000" min="0" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Price (€) *</label>
          <input style={{ ...selS, paddingRight: 12 }} type="number" value={form.price} onChange={set('price')} placeholder="e.g. 18500" min="0" required />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>NCT Expiry</label>
          <input style={{ ...selS, paddingRight: 12 }} value={form.nct} onChange={set('nct')} placeholder="e.g. 06/2026" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[['Seats','seats',[2,3,4,5,6,7]],['Doors','doors',[2,3,4,5]]].map(([l,k,opts]) => (
            <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{l}</label>
              <div style={{ position: 'relative' }}>
                <select style={selS} value={form[k]} onChange={set(k)}>{opts.map(n => <option key={n}>{n}</option>)}</select>
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 9 }}>▼</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Description</label>
        <textarea value={form.description} onChange={set('description')} placeholder="Describe the car — condition, history, features…"
          style={{ border: `1px solid ${T.surface}`, background: T.bg, padding: '10px 12px', fontSize: 'var(--step-sm)', minHeight: 110, fontFamily: 'inherit', resize: 'vertical', outline: 'none', color: T.ink }}
          onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.surface} />
      </div>

      <div>
        <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 12 }}>Features & Options</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {FEATURES_LIST.map(f => {
            const on = form.features?.includes(f);
            return (
              <button key={f} type="button" onClick={() => toggleFeat(f)}
                style={{ padding: '5px 12px', border: `1px solid ${on ? T.accent : T.surface}`, background: on ? T.accent : 'transparent', color: on ? T.accentInk : T.muted, fontSize: 'var(--step-xs)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', letterSpacing: '0.02em' }}>
                {on && '✓ '}{f}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: `1px solid ${T.surface}` }}>
        {onCancel && <Button variant="outline" onClick={onCancel} type="button">Cancel</Button>}
        <Button type="submit" loading={saving}>{initial.id ? 'Save Changes' : 'Add Listing'}</Button>
      </div>
    </form>
  );
}

/* ── Stat card ── */
function StatCard({ icon, label, value, sub }) {
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.surface}`, padding: '22px 24px', display: 'flex', gap: 16, alignItems: 'center' }}>
      <div style={{ width: 44, height: 44, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 400, color: T.ink, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 'var(--step-xs)', fontWeight: 600, color: T.muted, marginTop: 3, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</div>
        {sub && <div style={{ fontSize: 'var(--step-xs)', color: T.accent, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

/* ── Main Admin ── */
export default function Admin() {
  const { isAdmin, loading: authLoading, logout } = useAdmin();
  const { show, ToastContainer } = useToast();
  const [tab, setTab] = useState('dashboard');
  const [listings, setListings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [importReqs, setImportReqs] = useState([]);
  const [stats, setStats] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const loadData = async () => {
    setDataLoading(true);
    try {
      const [l, e, ir, s] = await Promise.all([api.getListings({ limit: 200 }), api.getEnquiries(), api.getImportRequests(), api.getStats()]);
      setListings(l.listings); setEnquiries(e); setImportReqs(ir); setStats(s);
    } catch {}
    setDataLoading(false);
  };

  useEffect(() => { if (isAdmin) loadData(); }, [isAdmin]);

  if (authLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner size={40} /></div>;
  if (!isAdmin) return <AdminLogin />;

  const handleAdd = async form => { setSaving(true); try { await api.createListing(form); await loadData(); setAddOpen(false); show('Listing added'); } catch { show('Failed', 'error'); } setSaving(false); };
  const handleEdit = async form => { setSaving(true); try { await api.updateListing(editListing.id, form); await loadData(); setEditListing(null); show('Listing updated'); } catch { show('Failed', 'error'); } setSaving(false); };
  const handleDelete = async id => { if (!window.confirm('Delete this listing?')) return; setDeletingId(id); try { await api.deleteListing(id); await loadData(); show('Deleted'); } catch { show('Failed', 'error'); } setDeletingId(null); };
  const markRead = async id => { try { await api.markEnquiryRead(id); setEnquiries(e => e.map(x => x.id === id ? { ...x, read: true } : x)); } catch {} };
  const delEnq = async id => { try { await api.deleteEnquiry(id); setEnquiries(e => e.filter(x => x.id !== id)); } catch {} };
  const updateImport = async (id, updates) => { try { await api.updateImportRequest(id, updates); await loadData(); } catch {} };
  const delImport = async id => { try { await api.deleteImportRequest(id); setImportReqs(r => r.filter(x => x.id !== id)); } catch {} };

  const filtered = statusFilter ? listings.filter(l => l.status === statusFilter) : listings;
  const unreadEnq = enquiries.filter(e => !e.read).length;
  const unreadImp = importReqs.filter(r => !r.read).length;

  const TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'listings',  label: `Stock (${listings.length})` },
    { id: 'enquiries', label: `Enquiries${unreadEnq > 0 ? ` · ${unreadEnq} new` : ''}` },
    { id: 'imports',   label: `Imports${unreadImp > 0 ? ` · ${unreadImp} new` : ''}` },
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.surface }}>
      <ToastContainer />

      {/* Admin nav */}
      <div style={{ background: T.ink, paddingInline: 'var(--margin)', display: 'flex', alignItems: 'center', height: 60, gap: 0, position: 'sticky', top: 0, zIndex: 100, borderBottom: `1px solid rgba(232,230,225,0.08)` }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 28, flexShrink: 0, textDecoration: 'none' }}>
          <div style={{ width: 28, height: 28, background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 11 }}>NA</span>
          </div>
          <span style={{ color: T.bg, fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-sm)', fontWeight: 400 }}>Admin</span>
        </Link>
        <nav style={{ display: 'flex', gap: 0, flex: 1 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '0 18px', height: 60, border: 'none', background: 'none', color: tab === t.id ? T.accent : 'rgba(232,230,225,0.55)', fontWeight: tab === t.id ? 600 : 400, fontSize: 'var(--step-xs)', cursor: 'pointer', letterSpacing: '0.04em', fontFamily: 'inherit', borderBottom: `2px solid ${tab === t.id ? T.accent : 'transparent'}`, transition: 'all 0.15s' }}>
              {t.label}
            </button>
          ))}
        </nav>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
          <Link to="/" style={{ fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.4)', letterSpacing: '0.04em' }}>View site →</Link>
          <Button variant="outline" size="sm" onClick={logout} style={{ color: T.bg, borderColor: 'rgba(232,230,225,0.2)', fontSize: 'var(--step-xs)' }}>Sign out</Button>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px var(--margin)' }}>

        {/* DASHBOARD */}
        {tab === 'dashboard' && (
          <div className="fade-up">
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem,3vw,2.5rem)', fontWeight: 400, color: T.ink, marginBottom: 28 }}>Dashboard</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, marginBottom: 32 }}>
              <StatCard icon="🚗" label="Cars in stock" value={stats.total || 0} sub={`${stats.sold || 0} sold`} />
              <StatCard icon="⚡" label="Electric" value={stats.electric || 0} />
              <StatCard icon="✉️" label="Enquiries" value={enquiries.length} sub={unreadEnq > 0 ? `${unreadEnq} unread` : undefined} />
              <StatCard icon="🔍" label="Import reqs" value={importReqs.length} sub={unreadImp > 0 ? `${unreadImp} new` : undefined} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {[{ title: 'Recent Enquiries', items: enquiries.slice(0, 6), tabId: 'enquiries', renderItem: e => ({ id: e.id, name: e.name, sub: e.message, meta: timeAgo(e.createdAt), unread: !e.read }) },
                { title: 'Import Requests',  items: importReqs.slice(0, 6), tabId: 'imports', renderItem: r => ({ id: r.id, name: r.name, sub: [r.make, r.model, r.year].filter(Boolean).join(' ') || 'Open request', meta: timeAgo(r.createdAt), unread: !r.read }) }
              ].map(({ title, items, tabId, renderItem }) => (
                <div key={tabId} style={{ background: T.bg, border: `1px solid ${T.surface}`, overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.surface}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ fontSize: 'var(--step-sm)', fontWeight: 600, color: T.ink, letterSpacing: '0.02em' }}>{title}</h2>
                    <button onClick={() => setTab(tabId)} style={{ background: 'none', border: 'none', color: T.accent, fontSize: 'var(--step-xs)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em' }}>View all →</button>
                  </div>
                  {items.length === 0 && <div style={{ padding: '32px 20px', textAlign: 'center', color: T.muted, fontSize: 'var(--step-sm)' }}>None yet</div>}
                  {items.map(item => { const r = renderItem(item); return (
                    <div key={r.id} style={{ padding: '12px 20px', borderBottom: `1px solid ${T.surface}`, display: 'flex', gap: 12, alignItems: 'flex-start', background: r.unread ? '#fefdf7' : T.bg }}>
                      <div style={{ width: 32, height: 32, background: r.unread ? T.accent : T.surface, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: r.unread ? '#fff' : T.muted, fontWeight: 600, fontSize: 'var(--step-xs)' }}>{r.name?.[0]?.toUpperCase()}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: 'var(--step-xs)', color: T.ink, letterSpacing: '0.02em' }}>{r.name}</span>
                          <span style={{ fontSize: 'var(--step-xs)', color: T.muted, flexShrink: 0 }}>{r.meta}</span>
                        </div>
                        <p style={{ fontSize: 'var(--step-xs)', color: T.muted, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sub}</p>
                      </div>
                    </div>
                  ); })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LISTINGS */}
        {tab === 'listings' && (
          <div className="fade-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem,3vw,2.5rem)', fontWeight: 400, color: T.ink, marginBottom: 4 }}>Stock</h1>
                <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em' }}>{listings.length} total listings</p>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 2 }}>
                  {[['','All'],['available','Available'],['reserved','Reserved'],['sold','Sold']].map(([val, label]) => (
                    <button key={val} onClick={() => setStatusFilter(val)}
                      style={{ padding: '7px 14px', border: `1px solid ${statusFilter === val ? T.accent : T.surface}`, background: statusFilter === val ? T.accent : 'transparent', color: statusFilter === val ? T.accentInk : T.muted, fontSize: 'var(--step-xs)', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em', transition: 'all 0.15s' }}>
                      {label}
                    </button>
                  ))}
                </div>
                <Button onClick={() => setAddOpen(true)}>+ Add listing</Button>
              </div>
            </div>

            {dataLoading ? <div style={{ textAlign: 'center', padding: 60 }}><Spinner size={36} /></div> : (
              <div style={{ background: T.bg, border: `1px solid ${T.surface}`, overflow: 'hidden' }}>
                {/* Header row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 100px 140px', gap: 12, padding: '12px 20px', background: T.surface, fontSize: 'var(--step-xs)', fontWeight: 700, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: `1px solid ${T.surface}` }}>
                  <span>Vehicle</span><span>Price</span><span>Mileage</span><span>Status</span><span>Actions</span>
                </div>
                {filtered.map(l => (
                  <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 100px 140px', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${T.surface}`, alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 'var(--step-sm)', color: T.ink }}>{l.year} {l.make} {l.model}</div>
                      <div style={{ fontSize: 'var(--step-xs)', color: T.muted, marginTop: 2, letterSpacing: '0.02em' }}>{l.fuelType} · {l.bodyType} · {l.colour}</div>
                      <div style={{ fontSize: 'var(--step-xs)', color: T.muted, marginTop: 1, opacity: 0.7 }}>👁 {l.views} · {timeAgo(l.publishedAt)}</div>
                    </div>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', fontWeight: 400, color: T.ink }}>{formatPrice(l.price)}</div>
                    <div style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{formatMileage(l.mileage)}</div>
                    <div>
                      <Badge variant={l.status === 'available' ? 'green' : l.status === 'sold' ? 'sold' : 'reserved'} style={{ fontSize: 10 }}>
                        {l.status === 'available' ? '✓ Live' : l.status === 'sold' ? '✕ Sold' : '⏳ Rsrvd'}
                      </Badge>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setEditListing(l)} style={{ background: T.surface, border: `1px solid ${T.surface}`, padding: '5px 10px', fontSize: 'var(--step-xs)', cursor: 'pointer', fontFamily: 'inherit', color: T.ink, transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = T.accent} onMouseLeave={e => e.currentTarget.style.borderColor = T.surface}>Edit</button>
                      <button onClick={() => window.open(`/car/${l.id}`, '_blank')} style={{ background: 'transparent', border: `1px solid ${T.surface}`, padding: '5px 10px', fontSize: 'var(--step-xs)', cursor: 'pointer', fontFamily: 'inherit', color: T.muted }}>View</button>
                      <button onClick={() => handleDelete(l.id)} disabled={deletingId === l.id}
                        style={{ background: 'transparent', border: `1px solid #fecaca`, padding: '5px 10px', fontSize: 'var(--step-xs)', cursor: 'pointer', fontFamily: 'inherit', color: '#991b1b' }}>
                        {deletingId === l.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <div style={{ padding: '48px 20px', textAlign: 'center', color: T.muted, fontSize: 'var(--step-sm)' }}>No listings found</div>}
              </div>
            )}
            <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add New Listing" width={680}><ListingForm onSubmit={handleAdd} loading={saving} onCancel={() => setAddOpen(false)} /></Modal>
            <Modal open={!!editListing} onClose={() => setEditListing(null)} title="Edit Listing" width={680}>{editListing && <ListingForm initial={editListing} onSubmit={handleEdit} loading={saving} onCancel={() => setEditListing(null)} />}</Modal>
          </div>
        )}

        {/* ENQUIRIES */}
        {tab === 'enquiries' && (
          <div className="fade-up">
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem,3vw,2.5rem)', fontWeight: 400, color: T.ink, marginBottom: 28 }}>
              Enquiries <span style={{ fontSize: 'var(--step-body)', fontFamily: 'Inter, sans-serif', fontWeight: 400, color: T.muted }}>({enquiries.length})</span>
            </h1>
            {enquiries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: T.muted, background: T.bg, border: `1px solid ${T.surface}` }}>No enquiries yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {enquiries.map(e => (
                  <div key={e.id} style={{ background: !e.read ? '#fefdf7' : T.bg, border: `1px solid ${!e.read ? T.accent : T.surface}`, padding: '22px 24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div style={{ width: 40, height: 40, background: !e.read ? T.accent : T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', color: !e.read ? '#fff' : T.muted, fontWeight: 600, fontSize: 'var(--step-sm)', flexShrink: 0 }}>{e.name?.[0]?.toUpperCase()}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 'var(--step-sm)', color: T.ink }}>{e.name}</div>
                          <div style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.02em' }}>{e.email}{e.phone ? ` · ${e.phone}` : ''}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                        {!e.read && <Badge variant="gold">New</Badge>}
                        <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em' }}>{timeAgo(e.createdAt)}</span>
                      </div>
                    </div>
                    <p style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.7, marginBottom: 16, padding: '12px 16px', background: T.surface }}>{e.message}</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <a href={`mailto:${e.email}?subject=Re: Your Northbridge Enquiry`}><Button size="sm" variant="dark">Reply by email</Button></a>
                      {e.phone && <a href={`tel:${e.phone}`}><Button size="sm" variant="outline">Call</Button></a>}
                      {e.phone && <a href={`https://wa.me/${e.phone.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer">
                        <Button size="sm" style={{ background: '#25D366', color: '#fff', border: 'none' }}>WhatsApp</Button></a>}
                      {!e.read && <Button size="sm" variant="ghost" onClick={() => markRead(e.id)}>Mark read</Button>}
                      <Button size="sm" variant="ghost" onClick={() => delEnq(e.id)} style={{ color: '#991b1b' }}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* IMPORTS */}
        {tab === 'imports' && (
          <div className="fade-up">
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem,3vw,2.5rem)', fontWeight: 400, color: T.ink, marginBottom: 28 }}>
              Import Requests <span style={{ fontSize: 'var(--step-body)', fontFamily: 'Inter, sans-serif', fontWeight: 400, color: T.muted }}>({importReqs.length})</span>
            </h1>
            {importReqs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: T.muted, background: T.bg, border: `1px solid ${T.surface}` }}>No import requests yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {importReqs.map(r => {
                  const statusC = { pending:'#92400e', searching:'#1e3a5f', found:'#4c1d95', completed:'#14532d' };
                  const statusBg = { pending:'#fffbeb', searching:'#eff6ff', found:'#f5f3ff', completed:'#f0fdf4' };
                  return (
                    <div key={r.id} style={{ background: !r.read ? '#fefdf7' : T.bg, border: `1px solid ${!r.read ? T.accent : T.surface}`, padding: '22px 24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <div style={{ width: 40, height: 40, background: !r.read ? T.ink : T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', color: !r.read ? T.bg : T.muted, fontWeight: 600, fontSize: 'var(--step-sm)', flexShrink: 0 }}>{r.name?.[0]?.toUpperCase()}</div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 'var(--step-sm)', color: T.ink }}>{r.name}</div>
                            <div style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{r.email} · {r.phone}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                          {!r.read && <Badge variant="gold">New</Badge>}
                          <span style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{timeAgo(r.createdAt)}</span>
                        </div>
                      </div>
                      {/* Spec grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 2, marginBottom: 16 }}>
                        {[['Looking for',[r.make,r.model].filter(Boolean).join(' ')||'—'],['Year',r.year||'Any'],['Budget',r.maxBudget||'—'],['Fuel',r.fuelType||'Any'],['Gearbox',r.transmission||'Any'],['Colour',r.colour||'Any']].map(([label,val]) => (
                          <div key={label} style={{ background: T.surface, padding: '10px 12px' }}>
                            <div style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
                            <div style={{ fontSize: 'var(--step-xs)', fontWeight: 600, color: T.ink }}>{val}</div>
                          </div>
                        ))}
                      </div>
                      {r.notes && <p style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.6, padding: '12px 14px', background: T.surface, marginBottom: 14 }}>{r.notes}</p>}
                      {/* Status + actions */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 4 }}>Status:</span>
                        {['pending','searching','found','completed'].map(s => (
                          <button key={s} onClick={() => updateImport(r.id, { status: s, read: true })}
                            style={{ padding: '5px 12px', border: `1px solid ${r.status === s ? statusC[s] : T.surface}`, background: r.status === s ? statusBg[s] : 'transparent', color: r.status === s ? statusC[s] : T.muted, fontSize: 'var(--step-xs)', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize', letterSpacing: '0.04em', transition: 'all 0.15s' }}>
                            {s}
                          </button>
                        ))}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                          <a href={`mailto:${r.email}`}><Button size="sm" variant="dark">Email</Button></a>
                          {r.phone && <a href={`tel:${r.phone}`}><Button size="sm" variant="outline">Call</Button></a>}
                          <Button size="sm" variant="ghost" onClick={() => delImport(r.id)} style={{ color: '#991b1b' }}>Delete</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
